import { useState, useEffect } from 'react'
import {
  type GridColDef,
  type GridPaginationModel,
  type GridSortItem,
} from '@mui/x-data-grid'
import { useGetPatronCheckins } from 'hooks/api/useGetPatronCheckins'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { DataTablePaging } from 'components/Shared/DataTablePaging'
import { formatDateTime } from 'utils/util'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { colorPrimitives } from 'components/Theme'
import { usePatronCheckInsDateRangeState } from 'stores/usePatronCheckInsDateRange'
import { formatPhoneNumber } from '@jjvgaming/player-payback-library'
import { useSetMe } from 'stores/useMeQuery'
import { ReportExportModal } from 'pages/PlayerPaybackReports/PlayerPaybackReportExportModal'
import { useSnackbar } from 'stores/useSnackbar'
import ExportIcon from 'assets/export.svg'

const columns: GridColDef[] = [
  {
    field: 'licensedEstablishmentName',
    headerName: 'Licensed Establishment',
    minWidth: 130,
    sortingOrder: ['asc', 'desc'],
    flex: 2,
    valueGetter: (params) => params.row.licensedEstablishment?.standardName,
  },
  {
    field: 'licensedEstablishmentLicenseNumber',
    headerName: 'License Number',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueGetter: (params) => params.row.licensedEstablishment?.licenseNumber,
  },
  {
    field: 'patronCheckinDate',
    headerName: 'Check in Time',
    minWidth: 130,
    sortingOrder: ['asc', 'desc'],
    flex: 2,
    valueGetter: (params) => params.row.createdOn,
    valueFormatter: ({ value }) => formatDateTime(value),
  },
  {
    field: 'patronId',
    headerName: 'Patron ID',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueFormatter: ({ value }) => `#${value}`,
  },
]

// PII permission columns
const columnsPII: GridColDef[] = [
  {
    field: 'licensedEstablishmentName',
    headerName: 'Licensed Establishment',
    minWidth: 400,
    sortingOrder: ['asc', 'desc'],
    flex: 2,
    valueGetter: (params) => params.row.licensedEstablishment?.standardName,
  },
  {
    field: 'licensedEstablishmentLicenseNumber',
    headerName: 'License Number',
    minWidth: 175,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueGetter: (params) => params.row.licensedEstablishment?.licenseNumber,
  },
  {
    field: 'patronCheckinDate',
    headerName: 'Check in Time',
    minWidth: 220,
    sortingOrder: ['asc', 'desc'],
    flex: 2,
    valueGetter: (params) => params.row.createdOn,
    valueFormatter: ({ value }) => formatDateTime(value),
  },
  {
    field: 'patronId',
    headerName: 'Patron ID',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueFormatter: ({ value }) => `#${value}`,
  },
  {
    field: 'firstName',
    headerName: 'First Name',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueGetter: (params) => params.row.patron.firstName,
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    minWidth: 120,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueGetter: (params) => params.row.patron.lastName,
  },
  {
    field: 'phoneNumber',
    headerName: 'Phone',
    minWidth: 140,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueGetter: (params) => params.row.patron.phoneNumber ?? '',
    valueFormatter: ({ value }) => formatPhoneNumber(value),
  },
  {
    field: 'email',
    headerName: 'Email',
    minWidth: 300,
    sortingOrder: ['asc', 'desc'],
    flex: 1,
    valueGetter: (params) => params.row.patron.email ?? '',
  },
  {
    field: 'addresses',
    minWidth: 400,
    headerName: 'Address',
    sortable: false,
    flex: 2,
    renderCell: (params) => {
      return (
        <p className="overflow-hidden whitespace-nowrap text-ellipsis">
          {(params.row.patron.addresses[0] &&
            `${params.row.patron.addresses[0].address1}` +
              (params.row.patron.addresses[0].address2
                ? `, ${params.row.patron.addresses[0].address2}`
                : '')) ??
            null}
        </p>
      )
    },
    valueGetter: (params) => {
      return (
        (params.row.patron.addresses[0] &&
          `${params.row.patron.addresses[0].address1}` +
            (params.row.patron.addresses[0].address2
              ? `, ${params.row.patron.addresses[0].address2}`
              : '')) ??
        null
      )
    },
  },
  {
    field: 'city',
    headerName: 'City',
    minWidth: 120,
    sortable: false,
    flex: 1,
    valueGetter: (params) =>
      params.row.patron.addresses.length
        ? params.row.patron.addresses[0].city
        : '',
  },
  {
    field: 'state',
    headerName: 'State',
    minWidth: 120,
    sortable: false,
    flex: 1,
    valueGetter: (params) =>
      params.row.patron.addresses.length
        ? params.row.patron.addresses[0].state
        : '',
  },
  {
    field: 'postalCode',
    headerName: 'Zip',
    minWidth: 120,
    sortable: false,
    flex: 1,
    valueGetter: (params) =>
      params.row.patron.addresses.length
        ? params.row.patron.addresses[0].postalCode
        : '',
  },
]

export const CheckedInPatronsTable = () => {
  const { startDate, endDate, licensedEstablishmentId } =
    usePatronCheckInsDateRangeState()
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  })
  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    { field: 'patronCheckinDate', sort: 'desc' },
  ])

  // map column names to a field that can be searched in the backend
  const fieldMapping: Record<string, string> = {
    licensedEstablishmentName: 'licensedEstablishment.standardName',
    licensedEstablishmentLicenseNumber: 'licensedEstablishment.licenseNumber',
    patronCheckinDate: 'createdOn',
    firstName: 'patron.firstName',
    lastName: 'patron.lastName',
    phoneNumber: 'patron.phoneNumber',
    email: 'patron.email',
    addresses: 'patron.addresses.address1',
    city: 'patronaddress.city',
    state: 'patronAddresses.state',
    postalCode: 'patronAddresses.postalCode',
  }

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize })
  }, [licensedEstablishmentId, startDate, endDate])
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const toggleModal = () => setIsModalOpen(!isModalOpen)
  const patronCheckinQuery = useGetPatronCheckins({
    currentPage: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    corporateAccountId: selectedCorporateAccountId,
    startDate,
    endDate,
    sortColumnName: fieldMapping[sortModel[0]?.field] || sortModel[0]?.field, // Convert field name
    sortOrder: sortModel[0]?.sort ?? 'asc',
    licensedEstablishmentId,
  })
  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model)
  }

  const onSortModelChange = (model: GridSortItem[]) => {
    setSortModel(model)
  }

  const leData =
    meQuery?.data?.user?.licensedEstablishments?.find(
      (x) =>
        x.licensedEstablishmentId ===
        meQuery?.data?.licensedEstablishments?.find(
          (x) => x.corporateAccountId === selectedCorporateAccountId
        )?.id
    ) ?? undefined

  const piiPermission = leData?.reportsAccessToPIIPermission
  const downloadPIIPermission = leData?.reportsDownloadPIIPermission

  if (meQuery.isPending || patronCheckinQuery.isPending) {
    return <CircularProgress />
  }

  if (meQuery.isError || patronCheckinQuery.isError) {
    return <p>An error occurred.</p>
  }

  if (patronCheckinQuery.data?.results?.length === 0) {
    return <NoDataIndicator />
  }

  return (
    <Stack spacing={2}>
      {patronCheckinQuery.data && (
        <>
          {downloadPIIPermission && (
            <Box className="flex lg:justify-end">
              <Button variant="contained" onClick={toggleModal}>
                <img
                  src={ExportIcon}
                  width={15}
                  height={15}
                  alt="Export icon"
                />
                <Typography variant="label-CTA">Export</Typography>
              </Button>
            </Box>
          )}
          <ReportExportModal
            isModalOpen={isModalOpen}
            toggleModal={toggleModal}
            reportName="Patron Check Ins"
            exportColumns={piiPermission ? columnsPII : columns}
            setMessage={setMessage}
            paginationModel={paginationModel}
            licensedEstablishmentId={licensedEstablishmentId}
            corporateAccountId={Number(selectedCorporateAccountId)}
            startDate={startDate}
            endDate={endDate}
            exportType="patronCheckIns"
          />
          <DataTablePaging
            slotProps={{
              row: {
                style: { cursor: 'default' },
              },
            }}
            noDataMessage="There are no patron accounts related to this corporate account"
            columns={piiPermission ? columnsPII : columns}
            rows={patronCheckinQuery.data?.results ?? []}
            totalCount={patronCheckinQuery.data?.rowCount ?? 0}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            sx={{
              color: colorPrimitives.black,
              '& .MuiDataGrid-row:hover': {
                cursor: 'auto',
              },
            }}
          />
        </>
      )}
    </Stack>
  )
}
